import { useQuery } from '@tanstack/react-query';
import { Activity, Building2, Percent, TrendingUp, Wallet } from 'lucide-react';
import { enumLabel, KpiCard, Money, Spinner, formatCompact } from '@riskpay/ui-components';
import type { Money as MoneyType } from '@riskpay/types';
import { api } from '../lib/client';

/* ==========================================================================
 *  Global telemetry
 *
 *  Two revenue lines are shown, deliberately: gross revenue is what merchants
 *  are charged, net revenue is what survives the acquirer's cut. A treasury
 *  view that reports only gross flatters the business and hides margin
 *  compression when the acquirer mix shifts.
 * ========================================================================== */

interface TelemetryResponse {
  window: string;
  today: {
    attempts: number;
    succeeded: number;
    failed: number;
    in_flight: number;
    success_rate: number;
    gtv: MoneyType;
    revenue: MoneyType;
  };
  attention: {
    kyc_waiting: number;
    disputes_open: number;
    accounts_to_verify: number;
    payments_stuck: number;
    frozen_merchants: number;
    webhooks_dead: number;
    routes_unpollable: number;
  };
  gtv: MoneyType;
  gross_revenue: MoneyType;
  acquirer_cost: MoneyType;
  net_revenue: MoneyType;
  attempts: number;
  successes: number;
  success_rate: number;
  by_method: { payment_method: string; attempts: number; successes: number; gtv: string }[];
  daily: { stat_date: string; gtv: string; net_revenue: string; success_rate_pct: number }[];
  merchants: { total: number; approved: number; pending: number; frozen: number };
  settlements: { pending: MoneyType; paid_30d: MoneyType };
}

/**
 * Today, and anything waiting on a person.
 *
 * The thirty-day figures below answer "how is the business doing" — a question
 * for a monthly review. Somebody opening this console in the morning is asking
 * two others: what is happening right now, and is anything queued for me.
 *
 * The attention row shows only non-zero counts. A wall of green zeroes trains
 * people to skim past it, and the one morning something is genuinely wrong it
 * would be skimmed past too. An empty row says everything is clear, in words.
 */
function TodayAndQueue({ today, attention }: Pick<TelemetryResponse, 'today' | 'attention'>) {
  const queue = [
    { key: 'kyc_waiting', label: 'KYC to review', n: attention.kyc_waiting, urgent: false },
    { key: 'payments_stuck', label: 'Payments stuck over 30 min', n: attention.payments_stuck, urgent: true },
    { key: 'disputes_open', label: 'Disputes open', n: attention.disputes_open, urgent: false },
    { key: 'accounts_to_verify', label: 'Bank accounts to verify', n: attention.accounts_to_verify, urgent: false },
    { key: 'frozen_merchants', label: 'Merchants frozen', n: attention.frozen_merchants, urgent: false },
    { key: 'webhooks_dead', label: 'Webhooks given up on', n: attention.webhooks_dead, urgent: true },
    /* A live route with no status URL is invisible to the sweep: its payments
       can only ever resolve on a callback, and a lost one strands them. */
    { key: 'routes_unpollable', label: 'Live routes with no status URL', n: attention.routes_unpollable, urgent: true },
  ].filter((q) => q.n > 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
      <section className="panel">
        <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3.5">
          <h2 className="text-base font-medium">Today · live</h2>
          <p className="text-xs text-content-subtle">
            {today.attempts === 0 ? (
              'No live payments yet'
            ) : (
              <>
                <span className="numeral text-content-muted">{today.attempts.toLocaleString('en-IN')}</span> attempted
                · <span className="numeral text-content-muted">{today.success_rate.toFixed(1)}%</span> succeeded
              </>
            )}
          </p>
        </header>

        {today.attempts > 0 && (
          <div className="flex h-1 w-full overflow-hidden" aria-hidden="true">
            <div className="bg-brand-500" style={{ width: `${(today.succeeded / today.attempts) * 100}%` }} />
            <div className="bg-danger" style={{ width: `${(today.failed / today.attempts) * 100}%` }} />
            <div className="bg-warning" style={{ width: `${(today.in_flight / today.attempts) * 100}%` }} />
          </div>
        )}

        <div className="grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
          {[
            { label: 'Succeeded', value: today.succeeded.toLocaleString('en-IN'), tone: 'text-content' },
            { label: 'Failed', value: today.failed.toLocaleString('en-IN'),
              tone: today.failed > 0 ? 'text-danger-text' : 'text-content' },
            { label: 'Still open', value: today.in_flight.toLocaleString('en-IN'),
              tone: today.in_flight > 0 ? 'text-warning-text' : 'text-content' },
            { label: 'Volume', value: <Money amount={today.gtv.amount} compact />, tone: 'text-content' },
          ].map((c) => (
            <div key={c.label} className="px-5 py-4">
              <p className="meta">{c.label}</p>
              <p className={`numeral mt-1.5 text-2xl font-semibold tracking-tight ${c.tone}`}>{c.value}</p>
            </div>
          ))}
        </div>
        <p className="border-t border-line px-5 py-2.5 text-xs text-content-subtle">
          Revenue today <Money amount={today.revenue.amount} className="text-content-muted" />
        </p>
      </section>

      <section className="panel">
        <header className="border-b border-line px-5 py-3.5">
          <h2 className="text-base font-medium">Needs a person</h2>
        </header>
        {queue.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-content-muted">
            Nothing waiting. Every queue is clear.
          </p>
        ) : (
          <ul className="divide-y divide-line-subtle">
            {queue.map((q) => (
              <li key={q.key} className="flex items-center justify-between gap-4 px-5 py-3">
                <span className="text-sm text-content-muted">{q.label}</span>
                <span
                  className={`numeral text-lg font-semibold ${q.urgent ? 'text-warning-text' : 'text-content'}`}
                >
                  {q.n.toLocaleString('en-IN')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TrendChart({ daily }: { daily: TelemetryResponse['daily'] }) {
  if (daily.length === 0) {
    return <div className="flex h-44 items-center justify-center text-xs text-content-faint">No live volume yet.</div>;
  }

  // The API returns newest-first; charts read left-to-right in time.
  const series = [...daily].reverse();
  const max = series.reduce((m, d) => Math.max(m, Math.round(Number(d.gtv))), 1);

  return (
    <div className="px-4 pb-4">
      <div className="flex h-44 items-end gap-1">
        {series.map((day) => {
          const rupees = Math.round(Number(day.gtv));
          return (
            <div
              key={day.stat_date}
              className="group flex-1"
              title={`${new Date(day.stat_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · ${formatCompact(day.gtv)} · ${day.success_rate_pct}% success`}
            >
              <div
                className="w-full rounded-sm bg-brand-500/60 transition-colors group-hover:bg-brand-500"
                style={{ height: `${Math.max((rupees / max) * 176, 2)}px` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-2xs text-content-faint">
        <span>{new Date(series[0]!.stat_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
        <span>Peak {formatCompact(max)} / day</span>
        <span>{new Date(series[series.length - 1]!.stat_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
      </div>
    </div>
  );
}

export function Telemetry() {
  const { data, isLoading } = useQuery({
    queryKey: ['telemetry'],
    queryFn: () => api.get<TelemetryResponse>('/telemetry'),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-content-muted">
        <Spinner /> Loading platform telemetry
      </div>
    );
  }

  const margin =
    Number(data.gross_revenue.amount) > 0
      ? (Number(data.net_revenue.amount) / Number(data.gross_revenue.amount)) * 100
      : 0;

  return (
    <div className="space-y-6">
      {/* Today and the queues first; the thirty-day picture is below. */}
      <TodayAndQueue today={data.today} attention={data.attention} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Gross transaction volume"
          value={<Money amount={data.gtv.amount} compact />}
          hint={`${data.window} · live mode`}
          icon={<Activity className="h-4 w-4" />}
        />
        <KpiCard
          label="Gross revenue"
          value={<Money amount={data.gross_revenue.amount} compact />}
          hint="MDR charged to merchants"
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <KpiCard
          label="Net revenue"
          value={<Money amount={data.net_revenue.amount} compact />}
          hint={`${margin.toFixed(1)}% margin after acquirer cost`}
          icon={<Wallet className="h-4 w-4" />}
        />
        <KpiCard
          label="Success rate"
          value={<span className="numeral">{data.success_rate.toFixed(2)}%</span>}
          hint={`${data.successes.toLocaleString('en-IN')} of ${data.attempts.toLocaleString('en-IN')}`}
          icon={<Percent className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <section className="panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="text-sm font-medium">Daily GTV · live mode</h2>
            <span className="text-2xs text-content-faint">Hover a bar for the day's detail</span>
          </div>
          <TrendChart daily={data.daily} />
        </section>

        <div className="space-y-4">
          <section className="panel">
            <div className="border-b border-line px-4 py-3">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Building2 className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
                Merchants
              </h2>
            </div>
            <dl className="grid grid-cols-2 gap-px bg-line">
              {[
                ['Total', data.merchants.total, ''],
                ['Approved', data.merchants.approved, 'text-brand-400'],
                ['In review', data.merchants.pending, 'text-[#FCD34D]'],
                ['Frozen', data.merchants.frozen, 'text-danger'],
              ].map(([label, value, tone]) => (
                <div key={label as string} className="bg-surface-raised px-4 py-3">
                  <dt className="text-2xs uppercase tracking-wider text-content-subtle">{label as string}</dt>
                  <dd className={`numeral mt-1 text-lg font-semibold ${tone as string}`}>{Number(value ?? 0)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="panel">
            <div className="border-b border-line px-4 py-3">
              <h2 className="text-sm font-medium">Settlement float</h2>
            </div>
            <dl className="divide-y divide-line-subtle">
              <div className="flex items-center justify-between px-4 py-3">
                <dt className="text-xs text-content">Awaiting payout</dt>
                <dd>
                  <Money amount={data.settlements.pending.amount} className="text-xs text-[#FCD34D]" />
                </dd>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <dt className="text-xs text-content">Paid, last 30 days</dt>
                <dd>
                  <Money amount={data.settlements.paid_30d.amount} className="text-xs" />
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <section className="panel">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-medium">Volume &amp; success by method</h2>
        </div>
        <ul className="divide-y divide-line-subtle">
          {data.by_method.map((row) => {
            const rate = row.attempts > 0 ? (row.successes / row.attempts) * 100 : 0;
            return (
              <li key={row.payment_method} className="flex items-center gap-4 px-4 py-3">
                <span className="w-32 shrink-0 text-xs text-content">{enumLabel(row.payment_method)}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
                  <div
                    className={`h-full rounded-full ${rate >= 85 ? 'bg-brand-500' : rate >= 70 ? 'bg-warning' : 'bg-danger'}`}
                    style={{ width: `${rate}%` }}
                  />
                </div>
                <span className="numeral w-16 shrink-0 text-right text-xs text-content-muted">{rate.toFixed(1)}%</span>
                <span className="numeral w-24 shrink-0 text-right text-xs text-content-subtle">
                  {formatCompact(row.gtv)}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
